import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    useReactTable,
    getCoreRowModel,
    flexRender,
    getFilteredRowModel,
    getExpandedRowModel,
} from "@tanstack/react-table";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import "./TanStackTable.css";

const TanStackTable = ({
    getTableColumns,
    columns,
    data,
    tableId,
    tableStyle = {},
    headerStyle = {},
    rowStyle = {},
    cellStyle = {},
    enableRowMove = false,
    onRowMove,
    expandAllByDefault=false,
    textAlign="left"
}) => {
    // console.log("TanStackTable-----------------------------", {columns, data, tableId, enableRowMove})
    const tableColumns = getTableColumns ? getTableColumns() : columns;
    const headerRef = useRef(null);
    const [expanded, setExpanded] = useState({});
    const onExpandedChange = useCallback(setExpanded, []);
    const memoizedColumns = useMemo(() => tableColumns, [tableColumns]);
    const memoizedData = useMemo(() => data, [data]);
    const [columnsWidth, setColumnsWidth] = useState([]) // Saving for manually set columns width on dragging a row to avoiding shrinking

    const table = useReactTable({
        data: memoizedData,
        columns: memoizedColumns,
        onExpandedChange,
        state: { expanded },
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getSubRows: (row) => row.subRows,
    });

    const handleDragEnd = (result) => {
        if (!result.destination || result.source.index === result.destination.index) return;
        if (onRowMove) {
            const sourceRow = table.getRowModel().rows[result.source.index].original;
            const destinationRow = table.getRowModel().rows[result.destination.index].original;
            onRowMove({ index: result.source.index, data: sourceRow }, { index: result.destination.index, data: destinationRow });
        }
    };

    useEffect(() => {
        if (!headerRef.current && columnsWidth?.length > 0) return;

        const observer = new ResizeObserver(() => {
          const widths = Array.from(headerRef.current.children).map((cell) =>
            (cell).getBoundingClientRect().width
          );
          setColumnsWidth(widths);
        });
    
        observer.observe(headerRef.current);

        if(expandAllByDefault) setExpanded(true); 
    
        return () => observer.disconnect();
    },[headerRef])

    return (
        <DragDropContext onDragEnd={enableRowMove ? handleDragEnd : undefined}>
            <table id={tableId} className="table_container" style={{ ...tableStyle }}>
                <thead className="table_header" style={{ ...headerStyle }}>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id} ref={headerRef}>
                            {headerGroup.headers.map((header) => (
                                <th key={header.id} style={{ textAlign: textAlign  }} className="table_heading_cells">
                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                {enableRowMove ? (
                    <Droppable droppableId="table-body" direction="vertical">
                        {(provided) => (
                            <tbody ref={provided.innerRef} {...provided.droppableProps} className="table_body">
                                {table.getRowModel().rows.length === 0 ? (
                                    <tr className="table_row no_data_row">
                                        <td colSpan={memoizedColumns.length} style={{ textAlign: 'center' }}>
                                            No data available
                                        </td>
                                    </tr>)
                                : table.getRowModel().rows.map((row, index) => (
                                    <Draggable
                                        key={row.id}
                                        draggableId={String(row.id)}
                                        index={index}
                                    >
                                        {(provided, snapshot) => (
                                            <tr
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                className="table_row"
                                                style={{ ...rowStyle, ...provided.draggableProps.style }}
                                            >
                                                {row.getVisibleCells().map((cell, index) => (
                                                    <td key={cell.id} style={snapshot.isDragging ? {width: `${columnsWidth?.[index] || 10}px`} : {}} className="table_cell">
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </td>
                                                ))}
                                            </tr>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </tbody>
                        )}
                    </Droppable>
                ) : (
                    <tbody className="table_body">
                        {table.getRowModel().rows.length === 0 ? (
                            <tr className="table_row no_data_row">
                                <td colSpan={memoizedColumns.length} style={{ textAlign: 'center' }}>
                                    No data available
                                </td>
                            </tr>)
                            :
                            table.getRowModel().rows.map((row) => (
                                <tr key={row.id} className="table_row" style={{ ...rowStyle }}>
                                    {row.getVisibleCells().map((cell) => (
                                        <td key={cell.id} style={{textAlign: textAlign}} className="table_cell">
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                    </tbody>
                )}
            </table>
        </DragDropContext>
    );
};

export default TanStackTable;
